import { useHttpClient } from "@/core/network/httpClient";
import { AxiosInstance, AxiosProgressEvent, AxiosResponse } from "axios";
import { CreateCourseRequestDto } from "../models/createCourseRequestDto";
import { MessageResponse } from "@/core/constant/messageResponse";
import { Course } from "../models/course";
import { UpdateCourseRequestDto } from "../models/updateCourseRequestDto";
import {
  CourseChapter,
  GetCourseChapterRequestDto,
} from "../models/courseChapter";
import { CreateChapterRequestDto } from "../models/createChapter";
import { UpdateChapterRequestDto } from "../models/updateChapterRequestDto";
import { GetLessonRequestDto, Lesson } from "../models/lesson";
import { CreateLessonRequestDto } from "../models/createLessonRequestDto";
import { CreateCourseContentRequestDto } from "../models/content";
import CoreResponseDto from "@/core/constant/CoreResponseDto";
import CorePagedResponse from "@/core/constant/CorePagedResponse";
import { IDefaultQueryParams } from "@/core/constant/defaultQueryParams";
import { UpdateLessonRequestDto } from "../models/updateLessonRequstDto";

export class CourseRepository {
  private static instance: CourseRepository;
  private httpClient: AxiosInstance;
  private readonly API_ENDPOINT = "/v1.0/management/Courses";

  private constructor(httpClient: AxiosInstance = useHttpClient()) {
    this.httpClient = httpClient;
  }

  public static getInstance(): CourseRepository {
    if (!CourseRepository.instance) {
      CourseRepository.instance = new CourseRepository();
    }
    return CourseRepository.instance;
  }

  async getCourseById(
    id: string
  ): Promise<AxiosResponse<CoreResponseDto<Course>>> {
    return await this.httpClient.get(`${this.API_ENDPOINT}/${id}`);
  }

  async updateCourse(
    id: string,
    course: UpdateCourseRequestDto
  ): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.put(`${this.API_ENDPOINT}/${id}`, course, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  async deleteCourse(id: string): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.delete(`${this.API_ENDPOINT}/${id}`);
  }

  async getCourses(
    request: IDefaultQueryParams
  ): Promise<AxiosResponse<CorePagedResponse<Course>>> {
    return await this.httpClient.get(this.API_ENDPOINT, {
      params: {
        ...request,
      },
    });
  }

  async createCourse(
    course: FormData
  ): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.post(this.API_ENDPOINT, course, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  async activateCourse(id: string): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.patch(`${this.API_ENDPOINT}/activation/${id}`);
  }

  async getChapters(
    request: GetCourseChapterRequestDto
  ): Promise<AxiosResponse<CorePagedResponse<CourseChapter>>> {
    return await this.httpClient.get(`${this.API_ENDPOINT}/chapters`, {
      params: {
        ...request,
      },
    });
  }
  async getChapter(
    courseId: string,
    chapterId: string
  ): Promise<AxiosResponse<CoreResponseDto<CourseChapter>>> {
    return await this.httpClient.get(
      `${this.API_ENDPOINT}/chapters/${courseId}/${chapterId}`
    );
  }

  async createChapter(
    courseId: string,
    request: CreateChapterRequestDto
  ): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.post(
      `${this.API_ENDPOINT}/chapters/${courseId}`,
      request
    );
  }

  async updateChapter(
    courseId: string,
    chapterId: string,
    request: UpdateChapterRequestDto
  ): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.put(
      `${this.API_ENDPOINT}/chapters/${courseId}/${chapterId}`,
      request
    );
  }

  async deleteChapter(
    courseId: string,
    chapterId: string
  ): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.delete(
      `${this.API_ENDPOINT}/chapters/${courseId}/${chapterId}`
    );
  }

  async getLessons(
    request: GetLessonRequestDto
  ): Promise<AxiosResponse<CorePagedResponse<Lesson>>> {
    return await this.httpClient.get(`${this.API_ENDPOINT}/lessons`, {
      params: {
        ...request,
      },
    });
  }
  async getLesson(
    courseId: string,
    chapterId: string,
    lessonId: string
  ): Promise<AxiosResponse<CoreResponseDto<Lesson>>> {
    return await this.httpClient.get(
      `${this.API_ENDPOINT}/lessons/${courseId}/${chapterId}/${lessonId}`
    );
  }

  async createLesson(
    courseId: string,
    chapterId: string,
    lesson: FormData
  ): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.post(
      `${this.API_ENDPOINT}/lessons/${courseId}/${chapterId}`,
      lesson,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  }

  async updateLesson(
    courseId: string,
    chapterId: string,
    lessonId: string,
    lesson: FormData
  ): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.put(
      `${this.API_ENDPOINT}/lessons/${courseId}/${chapterId}/${lessonId}`,
      lesson,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  }

  async deleteLesson(
    courseId: string,
    chapterId: string,
    lessonId: string
  ): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.delete(
      `${this.API_ENDPOINT}/lessons/${courseId}/${chapterId}/${lessonId}`
    );
  }

  async createContent(
    courseId: string,
    lessonId: string,
    request: FormData,
    onUploadProgress: (progressEvent: AxiosProgressEvent) => void
  ): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.post(
      `${this.API_ENDPOINT}/content/${courseId}/${lessonId}`,
      request,
      {
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        headers: {
          "Content-Type": "multipart/form-data",
          timeout: 999999999999999,
        },
        onUploadProgress: onUploadProgress,
      }
    );
  }

  async deleteContent(
    courseId: string,
    lessonId: string
  ): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.delete(
      `${this.API_ENDPOINT}/content/${courseId}/${lessonId}`
    );
  }
}
